import React, { useEffect, useRef, useState } from 'react';
import AOS from "aos";
import "aos/dist/aos.css";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaAward, FaUsers, FaHandshake, FaChartLine } from "react-icons/fa";
import { submitEnquiry } from "../utils/submitEnquiry";
import {
    canSubmitEnquiryForm,
    sanitizeIndianMobileInput,
} from "../utils/phoneValidation";
import bg from "../assets/bg.png";
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import pranjulMishra from "../assets/pranjul-mishra-about.jpeg";
import satyamVp from "../assets/teamImage/SatyamVp.jpeg";

gsap.registerPlugin(ScrollTrigger);

export default function About() {
    const [contactOpen, setContactOpen] = useState(false);
    const [enquirySubmitting, setEnquirySubmitting] = useState(false);
    const [callbackForm, setCallbackForm] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });
    const [interestOpen, setInterestOpen] = useState(false);
    const [interest, setInterest] = useState("I'm interested in");

    useEffect(() => {
        AOS.init({ duration: 800, once: true });
        window.scrollTo(0, 0);
    }, []);

    const callbackFormValid = canSubmitEnquiryForm(callbackForm);

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmitEnquiryForm(callbackForm)) return;

        const phone = sanitizeIndianMobileInput(callbackForm.phone);
        const payload = {
            name: callbackForm.name.trim(),
            email: callbackForm.email.trim(),
            phone,
            property:
                interest && interest !== "I'm interested in"
                    ? interest
                    : "General enquiry",
            message: (callbackForm.message || "").trim(),
            source: "About page — Contact Us",
            submittedAt: new Date().toISOString(),
        };

        setEnquirySubmitting(true);
        try {
            await submitEnquiry(payload);
            setContactOpen(false);
            setCallbackForm({ name: "", email: "", phone: "", message: "" });
            setInterest("I'm interested in");
        } catch (error) {
            console.error("Enquiry submission failed:", error);
            alert(
                error.message ||
                    "Sorry, we could not submit your enquiry right now. Please try again."
            );
        } finally {
            setEnquirySubmitting(false);
        }
    };

    const heroRef = useRef(null);
    const floatingRef = useRef([]);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Hero title animation
            gsap.from('.hero-title', {
                y: 100,
                opacity: 0,
                duration: 1.2,
                ease: 'power4.out',
                stagger: 0.2
            });

            // Badge animation
            gsap.from('.hero-badge', {
                scale: 0,
                opacity: 0,
                duration: 0.8,
                ease: 'back.out(1.7)',
                delay: 0.3
            });

            // Description animation
            gsap.from('.hero-desc', {
                y: 50,
                opacity: 0,
                duration: 1,
                ease: 'power3.out',
                delay: 0.5
            });

            // Stats animation
            gsap.from('.stat-card', {
                y: 60,
                opacity: 0,
                duration: 0.8,
                ease: 'power3.out',
                stagger: 0.15,
                delay: 0.8
            });

            // Floating elements animation
            floatingRef.current.forEach((el, i) => {
                gsap.to(el, {
                    y: -30,
                    duration: 2 + i * 0.5,
                    ease: 'power1.inOut',
                    repeat: -1,
                    yoyo: true,
                    delay: i * 0.3
                });
            });

            // Parallax effect for background shapes
            gsap.to('.bg-shape', {
                y: 100,
                scrollTrigger: {
                    trigger: heroRef.current,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 1
                }
            });

        }, heroRef);

        return () => ctx.revert();
    }, []);

    const stats = [
        { icon: FaUsers, value: '1200+', label: 'Happy Clients', color: 'from-blue-400 to-cyan-400' },
        { icon: FaAward, value: '9+', label: 'Years Experience', color: 'from-purple-400 to-pink-400' },
        { icon: FaHandshake, value: '90+', label: 'Society Sold', color: 'from-orange-400 to-red-400' },
        { icon: FaChartLine, value: '98%', label: 'Success Rate', color: 'from-green-400 to-emerald-400' }
    ];

    const team = [
        {
            name: "Pranjul Mishra",
            role: "Founder & CEO",
            image: pranjulMishra,
        },
        {
            name: "Satyam Prakhar",
            role: "Vice President",
            image: satyamVp,
        },
    ];

    return (
        <div className="min-h-screen font-sans text-slate-800 dark:text-slate-100 overflow-x-hidden bg-white dark:bg-slate-950">
            <Navbar />

            {/* HEADER */}
            <section ref={heroRef} className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#1a0f4d] via-[#3B2B9A] to-[#5a47d8]">
                {/* Animated Background Elements */}
                <div className="absolute inset-0 overflow-hidden">
                    {/* Gradient Orbs */}
                    <div className="bg-shape absolute top-20 -left-20 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl"></div>
                    <div className="bg-shape absolute bottom-20 -right-20 w-96 h-96 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 rounded-full blur-3xl"></div>
                    <div className="bg-shape absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-to-br from-[#3B2B9A]/20 to-transparent rounded-full blur-3xl"></div>

                    {/* Grid Pattern */}
                    <div className="absolute inset-0 opacity-10" style={{
                        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                        backgroundSize: '50px 50px'
                    }}></div>

                    {/* Floating Particles */}
                    {[...Array(20)].map((_, i) => (
                        <div
                            key={i}
                            ref={el => floatingRef.current[i] = el}
                            className="absolute w-2 h-2 bg-white/20 rounded-full"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                                animationDelay: `${Math.random() * 2}s`
                            }}
                        ></div>
                    ))}
                </div>

                <div className="max-w-7xl mx-auto px-6 py-20 relative z-10 w-full">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        {/* Left Content */}
                        <div className="space-y-8">
                            {/* Badge */}
                            <div className="hero-badge inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/10 backdrop-blur-xl border border-white/20 shadow-xl hover:scale-105 transition-transform cursor-pointer">
                                <div className="relative">
                                    <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse"></div>
                                    <div className="absolute inset-0 w-3 h-3 rounded-full bg-emerald-400 animate-ping"></div>
                                </div>
                                <span className="text-white font-semibold text-sm tracking-wide">Serving Since 2014</span>
                            </div>

                            {/* Main Heading */}
                            <div className="space-y-4">
                                <h1 className="text-6xl md:text-7xl lg:text-8xl font-black leading-[0.9]">
                                    <div className="hero-title text-white">About</div>
                                    <div className="hero-title">
                                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#D83B29] via-[#FF4500] to-[#8B1E1E] drop-shadow-2xl">
                                            Landmarks
                                        </span>
                                    </div>
                                    <div className="hero-title text-white">Realty</div>
                                </h1>
                            </div>

                            {/* Description */}
                            <p className="hero-desc text-xl md:text-2xl text-white/90 leading-relaxed max-w-xl font-light">
                                Real Estate Consulting That Guides Your Decisions
                            </p>

                            {/* CTA Buttons */}
                            <div className="hero-desc flex flex-wrap gap-4">
                                <button
                                    onClick={() => (window.location.href = "/properties")}
                                    className="group relative px-8 py-4 rounded-full font-bold text-white
  bg-gradient-to-r from-[#7A1616] via-[#9E1C1C] to-[#C62828]
  shadow-2xl hover:shadow-[#C62828]/50
  transition-all duration-300 hover:scale-105 overflow-hidden"
                                >
                                    <span className="relative z-10">Explore Properties</span>

                                    {/* Hover Layer (Left → Right) */}
                                    <div
                                        className="absolute inset-0
    bg-gradient-to-r from-[#5E0F0F] via-[#8B1E1E] to-[#D32F2F]
    -translate-x-full group-hover:translate-x-0
    transition-transform duration-500 ease-out"
                                    />
                                </button>

                                <button
                                    onClick={() => setContactOpen(true)}
                                    className="px-8 py-4 bg-white/10 backdrop-blur-xl border-2 border-white/30 rounded-full font-bold text-white hover:bg-white/20 transition-all duration-300 hover:scale-105">
                                    Contact Us
                                </button>
                            </div>
                        </div>

                        {/* Right Stats Cards */}
                        <div className="grid grid-cols-2 gap-6">
                            {stats.map((stat, index) => (
                                <div
                                    key={index}
                                    className="stat-card group relative"
                                >
                                    {/* Card Background with Hover Effect */}
                                    <div className="relative p-8 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/20 hover:bg-white/15 transition-all duration-500 hover:scale-105 hover:-translate-y-2 cursor-pointer overflow-hidden">
                                        {/* Gradient Overlay on Hover */}
                                        <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-20 transition-opacity duration-500`}></div>

                                        {/* Icon */}
                                        <div className={`relative mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.color} flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                                            <stat.icon className="text-white text-2xl" />
                                        </div>

                                        {/* Value */}
                                        <div className="relative text-5xl font-black text-white mb-2 group-hover:scale-110 transition-transform duration-300">
                                            {stat.value}
                                        </div>

                                        {/* Label */}
                                        <div className="relative text-white/70 font-medium text-sm tracking-wide">
                                            {stat.label}
                                        </div>

                                        {/* Shine Effect */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Bottom Wave Decoration */}
                <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]">
                    <svg className="relative block w-full h-24" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1200 120" preserveAspectRatio="none">
                        <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z"
                            className="fill-white/5"></path>
                    </svg>
                </div>

                {/* Scroll Indicator */}
                <button
                    type="button"
                    aria-label="Scroll to our story"
                    onClick={() =>
                        document.getElementById('our-story')?.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                        })
                    }
                    className="absolute bottom-8 left-1/2 z-30 -translate-x-1/2 cursor-pointer group rounded-full border-0 bg-transparent px-5 py-3 text-left hover:bg-white/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70"
                >
                    <span className="hero-scroll-affordance flex flex-col items-center gap-2 sm:gap-3">
                        <span className="text-white/60 text-[10px] sm:text-xs font-medium uppercase tracking-wider group-hover:text-white transition-colors">
                            Scroll to explore
                        </span>
                        <div className="w-6 h-9 sm:w-7 sm:h-11 border-2 border-white/40 rounded-full flex items-start justify-center p-1.5 sm:p-2 group-hover:border-white/80 transition-all">
                            <div className="hero-scroll-wheel w-1 h-2 sm:w-1.5 sm:h-3 rounded-full bg-white/70"></div>
                        </div>
                    </span>
                </button>
            </section>

            {/* OUR STORY */}
            <section
                id="our-story"
                className="scroll-mt-24 py-20 bg-white dark:bg-slate-950 sm:scroll-mt-28"
            >
                <div className="mx-auto px-6 lg:px-10">
                    <div className="grid md:grid-cols-2 gap-12 items-center">
                        <div data-aos="fade-right">
                            <h2 className="text-3xl md:text-4xl font-bold text-[#3B2B9A] mb-6">
                                Our Story
                                <span className="block w-20 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] mt-3 rounded-full"></span>
                            </h2>

                            <p className="text-slate-700 dark:text-slate-200 leading-relaxed mb-4">
                                Landmarks Realty was established with a clear purpose to make real estate decisions simpler and more transparent for buyers and investors. We are a <strong>RERA-approved and ISO-certified</strong> real estate consulting firm serving <strong>Gautam Buddha Nagar and the Yamuna Expressway</strong>, known for our focus on credibility and long-term value.
                            </p>

                            <p className="text-slate-700 dark:text-slate-200 leading-relaxed mb-4">
                                With experienced leadership at the helm, Landmarks Realty has earned recognition as a trusted consultant for both residential and commercial segments. Our advisory approach centers on clear guidance, verified information, and practical market understanding that helps clients move forward with confidence.
                            </p>

                            <p className="text-slate-700 dark:text-slate-200 leading-relaxed mb-4">
                                Supported by a knowledgeable team, we work with reputed developers and manage a wide range of residential and commercial properties. Our presence across key growth <strong>corridors in Gautam Buddha Nagar and along the Yamuna Expressway</strong> allows us to identify opportunities aligned with future growth and sound investment thinking.
                            </p>

                            <p className="text-slate-700 dark:text-slate-200 leading-relaxed">
                                At Landmarks Realty, every client relationship reflects responsibility and commitment. We focus on informed judgment so each property decision delivers lasting value.
                            </p>
                        </div>


                        <div
                            data-aos="fade-left"
                            className="relative max-w-full sm:max-w-lg lg:max-w-xl mx-auto"
                        >
                            {/* Image */}
                            <div className="rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl">
                                <img
                                    src={bg}
                                    alt="Our Story"
                                    className="
        w-full
        h-56 sm:h-72 lg:h-[400px]
        object-cover
      "
                                />
                            </div>

                            {/* Badge */}
                            <div
                                className="
      absolute
      -bottom-4 -left-4
      sm:-bottom-6 sm:-left-6
      bg-gradient-to-r from-[#8B1E1E] to-[#D83B29]
      text-white
      px-4 sm:px-6
      py-3 sm:py-4
      rounded-xl sm:rounded-2xl
      shadow-lg
    "
                            >
                                <div className="text-xl sm:text-2xl font-bold">9+</div>
                                <div className="text-xs sm:text-sm">Years of Trust</div>
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* MEET THE FOUNDER */}
            <section className="py-20 bg-slate-100 dark:bg-slate-800">
                <div className="mx-auto px-6">
                    {/* Section Header */}
                    <div className="text-center mb-16">
                        <h2
                            className="text-3xl md:text-4xl font-bold text-[#3B2B9A] inline-block relative"
                            data-aos="fade-up"
                        >
                            Meet the Founder
                            <span className="absolute left-1/2 -bottom-3 -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                        </h2>
                        <p
                            className="text-slate-600 dark:text-slate-300 mt-6 text-lg"
                            data-aos="fade-up"
                            data-aos-delay="100"
                        >
                            Real Estate Consultant at Landmarks Realty
                        </p>
                    </div>

                    {/* Row 1: Image (30%) + Introduction, Journey, Experience & Vision (70%) */}
                    <div className="grid lg:grid-cols-12 gap-8 mb-8 lg:items-start">
                        {/* Left: Image & Quote - 30% */}
                        <div data-aos="fade-right" className="lg:col-span-4 space-y-6">
                            {/* Main Image Container */}
                            <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                                <img
                                    // src="src/assets/images/pranjulMishra.jpg" // Replace with Pranjul's actual image
                                        src={pranjulMishra}
                                    alt="Pranjul Mishra"
                                    className="w-full aspect-[3/4] object-cover object-top group-hover:scale-105 transition-transform duration-500"
                                />
                                {/* Gradient Overlay */}
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>

                                {/* Name Badge on Image */}
                                <div className="absolute bottom-6 left-6 right-6">
                                    <h3 className="text-2xl font-bold text-white mb-1">Pranjul Mishra</h3>
                                    <p className="text-white/90 text-sm font-medium">Founder & Consultant</p>
                                </div>
                            </div>

                            {/* Quote Card */}
                            <div className="bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] rounded-2xl p-6 shadow-xl">
                                <div className="flex items-start gap-3">
                                    <svg className="w-8 h-8 text-white/30 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                                    </svg>
                                    <p className="text-white text-base italic font-light leading-relaxed">
                                        Property decisions shape futures, not just addresses.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Right: Introduction & Journey - 70% */}
                        <div data-aos="fade-left" className="lg:col-span-8 space-y-6">
                            {/* Introduction Card */}
                            <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-lg">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="w-1 h-12 bg-gradient-to-b from-[#3B2B9A] to-[#5A47D8] rounded-full"></div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-[#3B2B9A]">Pranjul Mishra</h3>
                                        <p className="text-slate-600 dark:text-slate-300">Property decisions shape futures, not just addresses</p>
                                    </div>
                                </div>

                                <p className="text-slate-700 dark:text-slate-200 leading-relaxed text-lg">
                                    Mr. Pranjul Mishra is a real estate consultant and entrepreneur, guiding homebuyers and investors across <strong className="text-[#3B2B9A]">Noida and the NCR region</strong>. With years of market experience, he leads <strong className="text-[#3B2B9A]">Landmarks Realty</strong>, a consulting firm focused on informed property decisions and long-term value creation.
                                </p>
                            </div>

                            {/* Journey Section */}
                            <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-lg">
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] flex items-center justify-center">
                                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                        </svg>
                                    </div>
                                    <h4 className="text-xl font-bold text-slate-800 dark:text-slate-100">The Journey</h4>
                                </div>

                                <p className="text-slate-700 dark:text-slate-200 leading-relaxed mb-4">
                                    He entered the real estate industry with a strong academic foundation, holding an <strong className="text-[#3B2B9A]">MBA from Integral University, Lucknow</strong>. Early in his career, Pranjul worked closely with developers, investors, and end clients, gaining practical insight into residential and commercial markets.
                                </p>

                                <p className="text-slate-700 dark:text-slate-200 leading-relaxed">
                                    His approach has always centered on <strong className="text-[#3B2B9A]">analysis and disciplined decision-making</strong> rather than volume sales.
                                </p>
                            </div>

                            {/* Experience & Impact + Vision — stacked full width under Journey */}
                            <div className="flex flex-col gap-6" data-aos="fade-up">
                                <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 sm:p-8 shadow-lg w-full">
                                    <div className="flex items-center gap-3 mb-5">
                                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] flex items-center justify-center flex-shrink-0">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                            </svg>
                                        </div>
                                        <h4 className="text-xl font-bold text-slate-800 dark:text-slate-100">Experience & Impact</h4>
                                    </div>

                                    <p className="text-slate-700 dark:text-slate-200 leading-relaxed text-sm sm:text-base">
                                        Over the years, Pranjul has advised <strong className="text-[#3B2B9A]">more than 200 families and investors</strong>, helping them navigate purchases with confidence. He has built trusted relationships with established developers in Noida and is known for providing structured investment guidance supported by market research and due diligence.
                                    </p>
                                </div>

                                <div className="bg-gradient-to-br from-[#3B2B9A]/5 to-[#5A47D8]/5 rounded-2xl p-6 sm:p-8 border border-[#3B2B9A]/10 w-full">
                                    <div className="flex items-center gap-3 mb-5">
                                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] flex items-center justify-center flex-shrink-0">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                            </svg>
                                        </div>
                                        <h4 className="text-xl font-bold text-slate-800 dark:text-slate-100">The Vision</h4>
                                    </div>

                                    <p className="text-slate-700 dark:text-slate-200 leading-relaxed mb-4 text-sm sm:text-base">
                                        With the belief that <em className="text-[#3B2B9A] font-semibold not-italic">"property choices shape financial futures"</em>, Pranjul founded Landmarks Realty in <strong className="text-[#3B2B9A]">January 2017</strong> to offer a more thoughtful alternative to traditional brokerage models.
                                    </p>

                                    <p className="text-slate-700 dark:text-slate-200 leading-relaxed text-sm sm:text-base">
                                        His work focuses on simplifying decisions, reducing risk, and helping clients move forward with confidence.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Row 3: Motivation (Full Width) */}
                    <div className="mb-8" data-aos="fade-up">
                        <div className="bg-white dark:bg-slate-900 rounded-2xl p-8 shadow-lg">
                            <p className="text-slate-700 dark:text-slate-200 leading-relaxed text-lg">
                                Outside of client work, Pranjul remains deeply engaged in studying market behavior, investment trends, and the evolving real estate landscape. He is motivated by the moment clients secure a home or investment they truly believe in, knowing the decision was made with purpose.
                            </p>
                        </div>
                    </div>

                    {/* Row 4: Key Highlights (Full Width) */}
                    <div data-aos="fade-up">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                            <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-[#3B2B9A]">
                                <div className="text-4xl font-bold text-[#3B2B9A] mb-2">200+</div>
                                <div className="text-sm text-slate-600 dark:text-slate-300 font-medium">Families Advised</div>
                            </div>
                            <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-[#5A47D8]">
                                <div className="text-4xl font-bold text-[#3B2B9A] mb-2">2017</div>
                                <div className="text-sm text-slate-600 dark:text-slate-300 font-medium">Company Founded</div>
                            </div>
                            <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-[#3B2B9A]">
                                <div className="text-4xl font-bold text-[#3B2B9A] mb-2">MBA</div>
                                <div className="text-sm text-slate-600 dark:text-slate-300 font-medium">Integral University</div>
                            </div>
                            <div className="bg-white dark:bg-slate-900 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-t-4 border-[#5A47D8]">
                                <div className="text-4xl font-bold text-[#3B2B9A] mb-2">Noida</div>
                                <div className="text-sm text-slate-600 dark:text-slate-300 font-medium">Service Region</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW LANDMARKS REALTY TEAM WORKS */}
            <section className="py-20 bg-white dark:bg-slate-950 relative overflow-hidden">
                {/* Decorative Background Elements */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#3B2B9A]/5 to-[#5A47D8]/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#5A47D8]/5 to-[#3B2B9A]/5 rounded-full blur-3xl"></div>

                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    {/* Section Header */}
                    <div className="text-center mb-16">
                        <h2
                            className="text-3xl md:text-4xl font-bold text-[#3B2B9A] inline-block relative"
                            data-aos="fade-up"
                        >
                            How Landmarks Realty Team Works
                            <span className="absolute left-1/2 -bottom-3 -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                        </h2>
                    </div>

                    {/* Main Content Card */}
                    <div
                        className="max-w-5xl mx-auto mb-10 sm:mb-12 px-4 sm:px-6"
                        data-aos="fade-up"
                        data-aos-delay="100"
                    >
                        <div
                            className="
      bg-gradient-to-br from-[#3B2B9A]/5 to-[#5A47D8]/5
      rounded-2xl sm:rounded-3xl
      p-6 sm:p-8 lg:p-10
      border border-[#3B2B9A]/10
      shadow-lg sm:shadow-xl
    "
                        >
                            <div className="flex items-start gap-3 sm:gap-4 mb-5 sm:mb-6">
                                {/* Icon */}
                                <div
                                    className="
          w-10 h-10 sm:w-12 sm:h-12
          rounded-lg sm:rounded-xl
          bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8]
          flex items-center justify-center
          flex-shrink-0
        "
                                >
                                    <svg
                                        className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>

                                {/* Content */}
                                <div>
                                    <p className="text-slate-700 dark:text-slate-200 leading-relaxed text-base sm:text-lg mb-3 sm:mb-4">
                                        At Landmarks Realty, real estate is treated as a{" "}
                                        <strong className="text-[#3B2B9A]">
                                            financial decision, not a sales pitch
                                        </strong>
                                        . We support residential buyers, commercial investors, and portfolio
                                        clients with advisory-led services that reduce risk and remove
                                        guesswork.
                                    </p>

                                    <p className="text-slate-700 dark:text-slate-200 leading-relaxed text-base sm:text-lg mb-3 sm:mb-4">
                                        Our team covers residential consulting, commercial guidance, investment
                                        planning, documentation support, and market research. Each engagement
                                        is handled with{" "}
                                        <strong className="text-[#3B2B9A]">
                                            focus, discretion, and accountability
                                        </strong>
                                        .
                                    </p>

                                    <p className="text-slate-700 dark:text-slate-200 leading-relaxed text-base sm:text-lg">
                                        Clients trust us because we stay objective.{" "}
                                        <strong className="text-[#3B2B9A]">
                                            Their interests always come first.
                                        </strong>
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* Service Areas Grid */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
                        {[
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                                    </svg>
                                ),
                                title: "Residential Consulting",
                                gradient: "from-blue-500 to-cyan-500"
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                ),
                                title: "Commercial Guidance",
                                gradient: "from-purple-500 to-pink-500"
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                ),
                                title: "Investment Planning",
                                gradient: "from-[#3B2B9A] to-[#5A47D8]"
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                ),
                                title: "Documentation Support",
                                gradient: "from-orange-500 to-red-500"
                            },
                            {
                                icon: (
                                    <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                ),
                                title: "Market Research",
                                gradient: "from-green-500 to-emerald-500"
                            }
                        ].map((service, index) => (
                            <div
                                key={index}
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                                className="group"
                            >
                                <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-slate-100 dark:border-slate-700 h-full">
                                    {/* Icon */}
                                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-4 group-hover:scale-110 group-hover:rotate-6 transition-all duration-300 mx-auto`}>
                                        {service.icon}
                                    </div>

                                    {/* Title */}
                                    <h4 className="text-center font-bold text-slate-800 dark:text-slate-100 text-base leading-tight">
                                        {service.title}
                                    </h4>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Bottom Trust Statement */}
                    <div className="mt-12 text-center" data-aos="fade-up" data-aos-delay="200">
                        <div className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white dark:bg-slate-900 border-2 border-[#3B2B9A]/20 shadow-lg">
                            <div className="w-3 h-3 rounded-full bg-[#3B2B9A] animate-pulse"></div>
                            <p className="text-slate-700 dark:text-slate-200 font-semibold">
                                <span className="text-[#3B2B9A]">Objective.</span>
                                <span className="mx-2">|</span>
                                <span className="text-[#3B2B9A]">Client-First.</span>
                                <span className="mx-2">|</span>
                                <span className="text-[#3B2B9A]">Trusted.</span>
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* TEAM SECTION */}
            <section className="py-20 bg-slate-100 dark:bg-slate-800 relative overflow-hidden">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="text-center mb-12">
                        <h2
                            className="text-3xl md:text-4xl font-bold text-[#3B2B9A] inline-block relative"
                            data-aos="fade-up"
                        >
                            Meet Our Team
                            <span className="absolute left-1/2 -bottom-3 -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                        </h2>
                        <p
                            className="text-slate-600 dark:text-slate-300 mt-6"
                            data-aos="fade-up"
                            data-aos-delay="100"
                        >
                            Experienced Real Estate Professionals Dedicated to Your Success
                        </p>
                    </div>

                    <div className="flex flex-wrap justify-center gap-8 lg:gap-12">
                        {team.map((member, index) => (
                            <div
                                key={index}
                                data-aos="fade-up"
                                data-aos-delay={index * 100}
                                className="relative w-full max-w-[280px]"
                            >
                                <div className="group relative overflow-hidden rounded-2xl shadow-lg cursor-pointer">
                                    <img
                                        src={member.image}
                                        alt={member.name}
                                        className="w-full h-80 object-cover object-top transition-transform duration-500 ease-out will-change-transform group-hover:scale-[1.12]"
                                    />
                                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                </div>
                                <div className="mt-4 text-center">
                                    <h3 className="text-lg font-bold text-slate-800 dark:text-slate-100">
                                        {member.name}
                                    </h3>
                                    <p className="text-sm text-[#3B2B9A] dark:text-indigo-300 font-medium">
                                        {member.role}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE LANDMARKS REALTY */}
            <section className="py-20 bg-white dark:bg-slate-950 relative overflow-hidden">
                {/* Decorative Background Elements */}
                <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-[#3B2B9A]/5 to-[#5A47D8]/5 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#5A47D8]/5 to-[#3B2B9A]/5 rounded-full blur-3xl"></div>

                <div className="max-w-7xl mx-auto px-6 relative z-10">
                    {/* Section Header */}
                    <div className="text-center mb-12">
                        <h2
                            className="text-3xl md:text-4xl font-bold text-[#3B2B9A] inline-block relative"
                            data-aos="fade-up"
                        >
                            Why Landmarks Realty Is the Smart Choice for Property Investors
                            <span className="absolute left-1/2 -bottom-3 -translate-x-1/2 w-20 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] rounded-full"></span>
                        </h2>
                    </div>

                    {/* Introduction Paragraph */}
                    <div className="max-w-5xl mx-auto mb-16" data-aos="fade-up" data-aos-delay="100">
                        <div className="bg-white dark:bg-slate-900 rounded-3xl p-8 shadow-lg border border-slate-200 dark:border-slate-600">
                            <p className="text-slate-700 dark:text-slate-200 leading-relaxed text-lg text-center">
                                Landmarks Realty blends <strong className="text-[#3B2B9A]">sharp market insight</strong> with hands-on deal experience to help investors spot solid opportunities and avoid costly missteps. With a focus on numbers and long‑term value, the team treats every purchase like a <strong className="text-[#3B2B9A]">serious business decision, not as a transaction</strong>.
                            </p>
                        </div>
                    </div>

                    {/* Three Key Features in One Row */}
                    <div className="grid md:grid-cols-3 gap-8">
                        {/* Smart Tech at Work */}
                        <div
                            data-aos="fade-up"
                            data-aos-delay="100"
                            className="group"
                        >
                            <div className="relative h-full bg-white dark:bg-slate-900 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-pointer overflow-hidden border border-slate-200 dark:border-slate-600">
                                {/* Gradient Background on Hover */}
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-cyan-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                {/* Icon Container */}
                                <div className="relative mb-6">
                                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    {/* Decorative Circle */}
                                    <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
                                </div>

                                {/* Content */}
                                <div className="relative">
                                    <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4 group-hover:text-[#3B2B9A] transition-colors duration-300">
                                        Smart Tech at Work
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                        Virtual meetings, digital paperwork, and fast status updates let you cover more ground with less effort. On‑time data on prices and neighborhood trends helps you pull the trigger at the right moment.
                                    </p>
                                </div>

                                {/* Shine Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            </div>

                            {/* Bottom Accent Line */}
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 group-hover:w-3/4 transition-all duration-500 rounded-full"></div>
                        </div>

                        {/* RERA-Approved */}
                        <div
                            data-aos="fade-up"
                            data-aos-delay="200"
                            className="group"
                        >
                            <div className="relative h-full bg-white dark:bg-slate-900 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-pointer overflow-hidden border border-slate-200 dark:border-slate-600">
                                {/* Gradient Background on Hover */}
                                <div className="absolute inset-0 bg-gradient-to-br from-purple-50 to-indigo-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                {/* Icon Container */}
                                <div className="relative mb-6">
                                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                    </div>
                                    {/* Decorative Circle */}
                                    <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br from-[#3B2B9A] to-[#5A47D8] opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
                                </div>

                                {/* Content */}
                                <div className="relative">
                                    <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4 group-hover:text-[#3B2B9A] transition-colors duration-300">
                                        RERA‑Approved
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                        Landmarks Realty focuses on RERA projects, giving you clear visibility on approvals, delivery timelines, and commitments. This framework increases buyer safety and keeps transactions transparent and accountable.                                    </p>
                                </div>

                                {/* Shine Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            </div>

                            {/* Bottom Accent Line */}
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-gradient-to-r from-[#3B2B9A] to-[#5A47D8] group-hover:w-3/4 transition-all duration-500 rounded-full"></div>
                        </div>

                        {/* ISO-Certified */}
                        <div
                            data-aos="fade-up"
                            data-aos-delay="300"
                            className="group"
                        >
                            <div className="relative h-full bg-white dark:bg-slate-900 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 cursor-pointer overflow-hidden border border-slate-200 dark:border-slate-600">
                                {/* Gradient Background on Hover */}
                                <div className="absolute inset-0 bg-gradient-to-br from-green-50 to-emerald-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                                {/* Icon Container */}
                                <div className="relative mb-6">
                                    <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                                        <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                                        </svg>
                                    </div>
                                    {/* Decorative Circle */}
                                    <div className="absolute -top-2 -right-2 w-12 h-12 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 opacity-20 group-hover:scale-150 transition-transform duration-500"></div>
                                </div>

                                {/* Content */}
                                <div className="relative">
                                    <h3 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4 group-hover:text-[#3B2B9A] transition-colors duration-300">
                                        ISO‑Certified
                                    </h3>
                                    <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                                        ISO‑based systems keep our service, documentation, and follow‑ups organized and consistent. You gain from reliable quality, professional handling, and a team dedicated to high standards every single day.
                                    </p>
                                </div>

                                {/* Shine Effect */}
                                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                            </div>

                            {/* Bottom Accent Line */}
                            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-gradient-to-r from-green-500 to-emerald-500 group-hover:w-3/4 transition-all duration-500 rounded-full"></div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />

            {contactOpen && (
                <>
                    <div
                        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 opacity-100 transition-opacity duration-300"
                        onClick={() => setContactOpen(false)}
                    ></div>

                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                        <form
                            onSubmit={handleContactSubmit}
                            className="lr-callback-panel animate-[fadeInScale_0.35s_ease-out]"
                        >
                            <button
                                type="button"
                                aria-label="Close"
                                onClick={() => setContactOpen(false)}
                                className="
                                    absolute -top-3 -right-3
                                    bg-white dark:bg-slate-900 backdrop-blur 
                                    text-gray-700 dark:text-gray-300
                                    shadow-md hover:shadow-lg
                                    w-9 h-9 flex items-center justify-center
                                    rounded-full border border-gray-200
                                    transition-all duration-300
                                    hover:scale-110 hover:rotate-90
                                "
                            >
                                ✕
                            </button>

                            <h3 className="text-2xl font-extrabold text-[#2D1B7E] dark:text-indigo-200 tracking-tight">
                                Request a Callback
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed">
                                Share your details and our team will call you within one business day.
                            </p>

                            <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    name="name"
                                    autoComplete="name"
                                    value={callbackForm.name}
                                    onChange={(e) =>
                                        setCallbackForm((prev) => ({
                                            ...prev,
                                            name: e.target.value,
                                        }))
                                    }
                                    placeholder="Full name"
                                    className="lr-callback-field"
                                />

                                <input
                                    type="email"
                                    name="email"
                                    autoComplete="email"
                                    value={callbackForm.email}
                                    onChange={(e) =>
                                        setCallbackForm((prev) => ({
                                            ...prev,
                                            email: e.target.value,
                                        }))
                                    }
                                    placeholder="Email address"
                                    className="lr-callback-field"
                                />

                                <input
                                    type="tel"
                                    name="phone"
                                    inputMode="numeric"
                                    autoComplete="tel"
                                    maxLength={10}
                                    value={callbackForm.phone}
                                    onChange={(e) =>
                                        setCallbackForm((prev) => ({
                                            ...prev,
                                            phone: sanitizeIndianMobileInput(
                                                e.target.value
                                            ),
                                        }))
                                    }
                                    placeholder="Mobile (10 digits, starts 6–9)"
                                    className="lr-callback-field"
                                />

                                <div className="relative" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        type="button"
                                        onClick={() => setInterestOpen(!interestOpen)}
                                        className="lr-callback-field flex items-center justify-between"
                                    >
                                        <span className={interest === "I'm interested in" ? "text-slate-400" : "text-slate-700 dark:text-slate-200"}>
                                            {interest}
                                        </span>

                                        <svg className={`w-4 h-4 transition-transform ${interestOpen ? "rotate-180" : ""}`}
                                            fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>

                                    {interestOpen && (
                                        <div className="absolute left-0 right-0 mt-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 border border-slate-300 dark:border-slate-600 rounded-lg shadow-xl z-50 overflow-hidden animate-fadeIn">
                                            {["Buying", "Selling", "Investment"].map((item) => (
                                                <div
                                                    key={item}
                                                    className="px-4 py-2 hover:bg-[#D83B29]/10 cursor-pointer text-slate-700 dark:text-slate-200"
                                                    onClick={() => {
                                                        setInterest(item);
                                                        setInterestOpen(false);
                                                    }}
                                                >
                                                    {item}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <textarea
                                name="message"
                                rows="4"
                                value={callbackForm.message}
                                onChange={(e) =>
                                    setCallbackForm((prev) => ({
                                        ...prev,
                                        message: e.target.value,
                                    }))
                                }
                                placeholder="Message (optional)"
                                className="lr-callback-field resize-none"
                            ></textarea>

                            <div className="mt-6 flex items-center justify-between">
                                <button
                                    type="submit"
                                    disabled={!callbackFormValid || enquirySubmitting}
                                    className="
        relative overflow-hidden group
        px-6 py-3 rounded-xl text-white font-semibold
        bg-gradient-to-r from-[#8B1E1E] to-[#D83B29]
        shadow-lg hover:shadow-xl
        hover:brightness-105 transition-all duration-300
        disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100
    "
                                >
                                    {enquirySubmitting ? "Sending..." : "Submit Request"}

                                    <span
                                        className="
            absolute inset-0 bg-white/20
            translate-x-[-120%]
            group-hover:translate-x-[120%]
            transition-all duration-700 ease-in-out
            skew-x-[45deg]
        "
                                    ></span>
                                </button>

                                <small className="text-gray-600 dark:text-gray-400">We respect your privacy.</small>
                            </div>
                        </form>
                    </div>

                    <style>
                        {`
                @keyframes fadeInScale {
                    0% { opacity: 0; transform: translateY(20px) scale(0.95); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}
                    </style>
                </>
            )}
        </div>
    );
}